/**
 * The firm's own people, and a lookup from `User` id to a name.
 *
 * Several screens store a bare `User` id — a client's responsible lawyer, a client's
 * owner, a critical date's owner — and have to render a person. `/users/` is the only
 * route that produces the firm's directory, so the lookup is built once here.
 *
 * TWO IDENTIFIERS, NOT ONE. `FirmMember.id` is the MEMBERSHIP id and is what the
 * `role`, `suspend` and `reinstate` sub-resources act on. `FirmMember.user` is the
 * global `User` id and is what every assignee field elsewhere refers to. Mixing them
 * produces a 404 that reads like a permissions problem.
 *
 * The query is disabled for a reader without `user.view`, so a lawyer opening a client
 * does not fire a request that can only come back 403. When it is disabled, or when it
 * simply has not answered yet, `nameFor` returns an em dash: an unresolved name is a
 * blank, never a claim that the field is empty.
 */

import { STAFF, qk, usePaginatedList, type FirmMember, type UUID } from '@legalcrm/api';
import { useMemo } from 'react';

import { useCan } from '@/auth/permissions';

export interface FirmMembersResult {
  members: FirmMember[];
  /** `true` when this reader may read the directory at all. */
  canView: boolean;
  isLoading: boolean;
  /** A `User` id — NOT a membership id — to a display name. */
  nameFor: (userId: UUID | null | undefined) => string;
}

export function useFirmMembers(options: { assignableOnly?: boolean } = {}): FirmMembersResult {
  const canView = useCan('user', 'view');

  // 100 is the server's maximum page size. A firm larger than that needs a picker that
  // searches server-side rather than a longer page, which is a change to make when a
  // firm that size exists rather than by guessing at it now.
  const list = usePaginatedList<FirmMember>(
    qk.list('users', { directory: true, assignable: options.assignableOnly ?? false }),
    STAFF.users,
    {
      limit: 100,
      assignable: options.assignableOnly ? 'true' : undefined,
    },
    { enabled: canView },
  );

  const byUserId = useMemo(() => {
    const index = new Map<string, FirmMember>();
    for (const member of list.items) index.set(member.user, member);
    return index;
  }, [list.items]);

  return {
    members: list.items,
    canView,
    isLoading: list.isLoading,
    nameFor: (userId) => {
      if (!userId) return '—';
      const member = byUserId.get(userId);
      return member?.full_name || member?.email || '—';
    },
  };
}
